import {
  Box,
  makeStyles,
  TableBody,
  TableCell,
  TableRow,
  Theme,
  useTheme,
} from '@material-ui/core';
import { Row } from '@tanstack/react-table';
import clsx from 'clsx';
import {
  CSSProperties,
  MouseEvent,
  ReactNode,
  memo,
  useCallback,
  useMemo,
} from 'react';
import { WbExpandToggle, WbExpandToggleProps } from '../../WbExpandToggle';
import { useWbTableContext } from '../context/WbTableContext';
import { Selector } from '../Selector';
import { TableCellProps, TableContentProps } from '../types';
import { explodeTreeRows } from '../utils';
import { DefaultCell } from '../StandardCells/DefaultCell';
import { WbTableCell } from '../WbTableCell';
import { getCommonPinningStyles } from '../utils/columnPinningUtils';
import { useTableColumnPinning } from '../hooks/useTableState';
import { ACTIONS_COLUMN_ID } from '../StandardColumns';

const useTreeCellStyles = makeStyles(theme => ({
  tableTreeCellRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  expandIcon: {
    fontSize: theme.typography.body1.fontSize,
  },
}));

const useRowStyles = makeStyles<Theme, { disableRowHighlightOnHover: boolean }>(
  theme => ({
    tableRow: props => ({
      position: 'relative',
      borderBottom: '1px solid white',
      backgroundColor: theme.palette.background.default,
      '&:hover': {
        backgroundColor: props.disableRowHighlightOnHover
          ? undefined
          : theme.palette.bkg.primary,
      },
    }),
    tableSelectorCell: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1),
      userSelect: 'none',
      touchAction: 'none',
      width: '50px',
      overflow: 'hidden',
      textOverflow: 'clip',
    },
  }),
  { name: 'WbTableContent' },
);

interface TableTreeCellProps extends WbExpandToggleProps {
  children: ReactNode;
  depth: number;
  hasChildren: boolean;
}

function TableTreeCell({
  children,
  expanded,
  onClick,
  depth,
  hasChildren,
}: TableTreeCellProps) {
  const classes = useTreeCellStyles();
  const theme = useTheme();

  const paddingLeft = useMemo(() => theme.spacing(2) * depth, [theme, depth]);

  const iconStyle = useMemo(
    () => ({
      visibility: hasChildren ? ('visible' as const) : ('hidden' as const),
    }),
    [hasChildren],
  );

  return (
    <Box className={classes.tableTreeCellRoot} style={{ paddingLeft }}>
      <WbExpandToggle
        expanded={expanded}
        onClick={onClick}
        iconClassName={classes.expandIcon}
        style={iconStyle}
      />
      {children}
    </Box>
  );
}

const MemoizedTableTreeCell = memo(TableTreeCell);

interface TableCellComponentProps<T> {
  row: T;
  cell: TableCellProps<T>;
  rowDepth?: number;
}

export function TableCellComponent<T>({
  row,
  cell,
  rowDepth = 0,
}: TableCellComponentProps<T>) {
  if (cell.fieldRender) {
    return cell.fieldRender(row, { rowDepth });
  }
  const field = cell.field;

  if (!field) return null;

  const formattedValue = cell.formatter
    ? cell.formatter(row[field])
    : row[field];

  return <DefaultCell value={formattedValue} {...cell} />;
}

const MemoizedTableCellComponent = memo(
  TableCellComponent,
) as typeof TableCellComponent;

interface TableRowComponentProps<T> {
  row: Row<T>;
  index: number;
  columns: TableCellProps<T>[];
  selection: string | undefined;
  selectedItems: T[] | undefined;
  setSelectedItems: ((items: T[]) => void) | undefined;
  tooltipIfDisabled: string | undefined;
  onRowClick?: any;
  rowDynamicClassName: ((row: T) => string | undefined) | undefined;
  treeProps: any;
  getRowId: ((row: T) => string) | undefined;
  checkIfDisabled: ((row: T) => boolean) | undefined;
  disableRowHighlightOnHover: boolean;
  theme: Theme;
  rowStyle: CSSProperties | undefined;
  rowClassName: string | undefined;
  depthMap: Map<string, number>;
  isAnyRowNested: boolean;
  tableSettings: any;
}

function TableRowComponent<T>({
  row,
  index,
  columns,
  selection,
  selectedItems,
  setSelectedItems,
  tooltipIfDisabled,
  onRowClick,
  rowDynamicClassName,
  disableRowHighlightOnHover,
  treeProps,
  getRowId,
  checkIfDisabled,
  theme,
  rowStyle,
  rowClassName,
  depthMap,
  isAnyRowNested,
  tableSettings,
}: TableRowComponentProps<T>) {
  const id = getRowId?.(row.original);
  const classes = useRowStyles({ disableRowHighlightOnHover });

  const computedRowStyle = useMemo(() => {
    const baseStyle = {
      ...(onRowClick ? { cursor: 'pointer' } : {}),
      ...rowStyle,
    };

    const isSelected =
      selection === 'single' &&
      (selectedItems?.length ?? 0) > 0 &&
      getRowId?.(selectedItems![0]) === getRowId?.(row.original);

    const isMultiSelected =
      selection === 'multiple' &&
      selectedItems?.find(r => getRowId?.(r) === getRowId?.(row.original));

    if (isSelected || isMultiSelected) {
      return {
        ...baseStyle,
        backgroundColor: theme.palette.bkg.primary,
      };
    }

    return baseStyle;
  }, [
    onRowClick,
    rowStyle,
    selection,
    selectedItems,
    getRowId,
    row.original,
    theme.palette.bkg.primary,
  ]);

  const handleRowClick = useCallback(
    (event: MouseEvent) => {
      let targetElement: HTMLElement | null = event.target as HTMLElement;
      while (targetElement) {
        if (targetElement.hasAttribute('block-row-click')) {
          return;
        }
        targetElement = targetElement.parentElement;
      }
      event.preventDefault();
      event.stopPropagation();
      if (onRowClick) onRowClick(row.original, event, index);
      if (selection === 'single') setSelectedItems?.([row.original]);
    },
    [onRowClick, row.original, index, selection, setSelectedItems],
  );

  const handleExpandClick = useCallback(() => {
    if (!id || !treeProps) return;

    const expanded = treeProps.expandedRowsIds.includes(id);
    if (expanded) {
      treeProps.onExpandChange(
        treeProps.expandedRowsIds.filter((i: string) => i !== id),
      );
    } else {
      treeProps.onExpandChange([...treeProps.expandedRowsIds, id]);
    }
  }, [id, treeProps]);

  const rowDepth = id ? depthMap.get(id) ?? 0 : 0;

  // Use robust hook to track column pinning changes
  const columnPinningState = useTableColumnPinning(tableSettings);

  const tableCells = useMemo(() => {
    return columns.map((cell, cellIndex) => {
      let content = (
        <MemoizedTableCellComponent<T>
          row={row.original}
          cell={cell}
          rowDepth={rowDepth}
        />
      );

      if (id && treeProps && cellIndex === 0 && getRowId && isAnyRowNested) {
        const expanded = treeProps.expandedRowsIds.includes(id);
        const hasChildren = treeProps.getRowChildren(row.original).length > 0;

        content = (
          <MemoizedTableTreeCell
            hasChildren={hasChildren}
            expanded={expanded}
            onClick={handleExpandClick}
            depth={rowDepth}
          >
            {content}
          </MemoizedTableTreeCell>
        );
      }

      // Get the column from tableSettings for pinning styles
      const uniqueFieldId =
        cell.uniqueFieldId || String(cell.field).replaceAll('.', '_');
      const column = tableSettings.getColumn(uniqueFieldId);

      const isPinned = column.getIsPinned();

      const cellStyle = {
        textAlign:
          cell.uniqueFieldId === ACTIONS_COLUMN_ID
            ? ('right' as const)
            : ('left' as const),
        ...(isPinned ? getCommonPinningStyles(column, theme, false) : {}),
      };

      return (
        <WbTableCell key={`cell-index-${cellIndex}`} style={cellStyle}>
          {content}
        </WbTableCell>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columns,
    row.original,
    rowDepth,
    id,
    treeProps,
    getRowId,
    isAnyRowNested,
    handleExpandClick,
    theme,
    tableSettings,
    columnPinningState,
  ]);

  return (
    <TableRow
      className={clsx(
        rowDynamicClassName?.(row.original),
        classes.tableRow,
        rowClassName,
      )}
      style={computedRowStyle}
      onClick={handleRowClick}
    >
      {selection === 'multiple' && (
        <TableCell
          key={`${index}-selector`}
          className={classes.tableSelectorCell}
        >
          <Selector
            row={row.original}
            getRowId={getRowId!}
            selectedItems={selectedItems!}
            setSelectedItems={setSelectedItems!}
            disabled={checkIfDisabled?.(row.original)}
            tooltipIfDisabled={tooltipIfDisabled}
          />
        </TableCell>
      )}
      {tableCells}
    </TableRow>
  );
}

// TODO find a way to avoid using isEqual here, as it can be expensive
const MemoizedTableRowComponent = memo(
  TableRowComponent,
) as typeof TableRowComponent;

export function TableContent<T>({
  rowStyle,
  rowClassName,
}: TableContentProps<T>) {
  const theme = useTheme();

  const {
    columns,
    selection,
    selectedItems,
    setSelectedItems,
    tooltipIfDisabled,
    onRowClick,
    rowDynamicClassName,
    treeProps,
    getRowId,
    disableRowHighlightOnHover,
    checkIfDisabled,
    tableSettings,
    RowsWrapper,
  } = useWbTableContext<T>();

  const {
    rows: displayedRows,
    isAnyRowNested,
    depthMap,
  } = useMemo(() => {
    const modelRows = tableSettings.getRowModel().rows;

    if (!treeProps?.getRowChildren || !getRowId) {
      return {
        rows: modelRows,
        isAnyRowNested: false,
        depthMap: new Map<string, number>(),
      };
    }

    return explodeTreeRows(
      modelRows,
      getRowId,
      treeProps.getRowChildren,
      treeProps.expandedRowsIds ?? [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    tableSettings.getRowModel().rows,
    getRowId,
    treeProps?.getRowChildren,
    treeProps?.expandedRowsIds,
  ]);

  return (
    <TableBody>
      {displayedRows.map((row: Row<T>, index: number) => (
        <RowsWrapper
          data={row.original}
          key={getRowId ? getRowId(row.original) : `table-row-${index}`}
        >
          <MemoizedTableRowComponent
            row={row}
            index={index}
            columns={columns}
            selection={selection}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            tooltipIfDisabled={tooltipIfDisabled}
            onRowClick={onRowClick}
            rowDynamicClassName={rowDynamicClassName}
            treeProps={treeProps}
            getRowId={getRowId}
            checkIfDisabled={checkIfDisabled}
            disableRowHighlightOnHover={disableRowHighlightOnHover}
            theme={theme}
            rowStyle={rowStyle}
            rowClassName={rowClassName}
            depthMap={depthMap}
            isAnyRowNested={isAnyRowNested}
            tableSettings={tableSettings}
          />
        </RowsWrapper>
      ))}
    </TableBody>
  );
}
